import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { PurposePicker, TagChips, purposePicks } from '../../components/common/EventTags';
import { apiRequest, shortDate, wallClockTime } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './HostEvent.css';

/* What the COMPANY decides about the event, as opposed to what the event IS.

   It used to be the last card on /host-event, under the run sheet and the surveys — which put a
   delete button at the bottom of the screen somebody stands in front of a room with, and pushed
   the things they actually need that evening further down the page. It is its own screen now,
   reached by the cog in the host screen's header.

   ★ It did NOT move to the editor, and that is the whole point of it being here. Three people
   open an event: the engagement ADMIN owns it, the internal HOST runs it, and the external host
   — the facilitator we send in — opens the EDITOR and nothing else. What kind of event this is,
   what it is FOR, whether people may register, whether it is published, cancelled or deleted are
   the owning company's decisions, so they sit behind the host gate rather than the editor's.

   ★ What it is FOR — the themes, values and UN goals out of the company's own Programmes — was
   on the editor until it was moved here, for exactly that reason: both chip rows are the
   COMPANY's vocabulary, and having its categories here and its purpose two screens away was one
   question asked in two places. They save together now, in one PUT.

   ★ Deleting is the admin's alone (`can_manage`), and it only ever works on an event nobody has
   touched — the platform refuses one with people, orders, tickets or photos on it. Cancelling is
   the host's too, because they are the one who knows it is off. See routes/company_events. */

/* The three switches that decide who may put their name down and who gets to see the list. Read
   off the event once and written back together, because they only ever make sense as a set. */
const REG_FIELDS = ['registration_enabled', 'show_registered_count',
  'show_registered_names', 'is_invite_only', 'hide_from_non_invited',
  'send_day_before_mail', 'members_only'];

/* ★ Which of them are ON when the payload does not mention them. Every switch here is off by
   default except the day-before reminder, which is an opt-OUT: reading a missing value as false
   would show it unticked and then SAVE that, quietly turning off a reminder nobody asked to
   turn off. */
const REG_DEFAULTS = { send_day_before_mail: true };

const regOf = (event) => REG_FIELDS.reduce(
  (out, field) => ({
    ...out,
    [field]: (event && event[field] !== undefined)
      ? !!event[field]
      : !!REG_DEFAULTS[field],
  }), {});

const HostEventSettings = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();

  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tags, setTags] = useState(null);
  // What the event is FOR, as `{theme: [id], value: [id], sdg: [id]}`. Its own state rather than
  // part of the switches: it is three lists the API names separately, not a field.
  const [purpose, setPurpose] = useState(null);
  const [reg, setReg] = useState(null);
  // ★ OFF by default. Publishing puts the event out there; this box is what pushes it into
  // inboxes, and that is the choice you make deliberately rather than discover afterwards.
  const [notify, setNotify] = useState(false);
  // ★ ON by default for a series. "Every Thursday" is one decision made ten times, so the box is
  // there to NARROW it to this one date, which is the rarer intent.
  const [applySeries, setApplySeries] = useState(true);
  const [notice, setNotice] = useState(null);

  // Does a COMPANY own this event? A public Bravos Lab does not, and the purpose chips are the
  // company's own — what it declares under Programmes. Read off the payload rather than inferred
  // from an empty catalogue: no catalogue reads as "you have not set any up yet", which is a
  // different sentence.
  const isCompanyEvent = !!(data && data.is_company_event);

  /* The host payload, not a settings-shaped one of its own. It already carries the event, the
     BU's category catalogue and `can_manage` behind exactly the gate this screen needs, and a
     second endpoint would be a second copy of that gate to keep in step. */
  const load = useCallback(() => {
    apiRequest('GET', `/company/event/${eventId}/host`)
      .then((res) => {
        setData(res);
        setTags((cur) => cur || ((res.event && res.event.tags) || []).map((x) => x.id));
        setPurpose((cur) => cur || purposePicks(res.event && res.event.purpose));
        setReg((cur) => cur || regOf(res.event));
      })
      // Not the admin, not the host, or not this company's event — none of which this screen can
      // do anything about.
      .catch(() => navigate(`/public-event/${eventId}`));
  }, [eventId, navigate]);

  useEffect(() => { load(); }, [load]);

  const act = (promise, done) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    promise
      .then(done)
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  /* Categories, purpose and registration, saved together — they are one question: what kind of
     event this is, what it is for and who may put their name down. Never mails anybody: the send
     only fires on a change to the date or the place, and neither is on this screen. */
  const saveSetup = () => {
    if (tags === null || reg === null) return;
    act(apiRequest('PUT', `/company/event/${eventId}`,
                   { tags, ...reg, apply_to_series: applySeries,
                     // Only where a company owns the event. The ids come out of ONE company's
                     // catalogue, and the picker is hidden on a public one — sending three empty
                     // lists anyway would read as "clear what this event is for".
                     ...(isCompanyEvent && purpose
                       ? { themes: purpose.theme, values: purpose.value, sdgs: purpose.sdg }
                       : {}) }),
        (res) => { setData((d) => ({ ...d, event: res.event })); setNotice(t('saved')); });
  };

  const togglePurpose = (kind, id) => setPurpose((p) => {
    const on = (p || {})[kind] || [];
    return { ...p, [kind]: on.includes(id) ? on.filter((x) => x !== id) : [...on, id] };
  });

  const publish = (isPublic) => {
    act(apiRequest('POST', `/company/event/${eventId}/publish`,
                   { is_public: isPublic, notify: isPublic && notify,
                     apply_to_series: applySeries }),
        (res) => {
          setData((d) => ({ ...d, event: res.event }));
          // Two numbers worth saying out loud: how many people were invited, and how many
          // other dates in the series went live with this one.
          const parts = [];
          if (res.invited) parts.push(t('invitedCount', { n: res.invited }));
          if (res.series_published) {
            parts.push(t('seriesPublished', { n: res.series_published }));
          }
          setNotice(parts.length ? parts.join(' ') : t('saved'));
        });
  };

  const setCancelled = (isCancelled) => {
    // eslint-disable-next-line no-alert
    if (isCancelled && !window.confirm(t('cancelEventConfirm'))) return;
    act(apiRequest('POST', `/company/event/${eventId}/cancel`, { is_cancelled: isCancelled }),
        (res) => {
          setData((d) => ({ ...d, event: res.event }));
          setNotice(res.told ? t('toldRegistered', { n: res.told }) : t('saved'));
        });
  };

  const remove = () => {
    // eslint-disable-next-line no-alert
    if (busy || !window.confirm(t('deleteEventConfirm'))) return;
    setBusy(true);
    setNotice(null);
    apiRequest('DELETE', `/company/event/${eventId}`)
      .then(() => navigate((data && data.event && data.event.programme_id)
        ? `/programme/${data.event.programme_id}` : '/programmes'))
      // The platform refuses to delete an event people are already on. That is the right
      // answer, and the message says to cancel it instead.
      .catch(() => { setNotice(t('deleteEventInUse')); setBusy(false); });
  };

  if (!data || tags === null || reg === null || purpose === null) {
    return (
      <Base activeNav="home">
        <Card>
          <Back to={`/host-event/${eventId}`} />
          <Title my="1" className="mt-3">
            <Icon name="settings" theme="primary" className="me-2" />
            {t('eventSettings')}
          </Title>
          <p className="bravos-q__hint mb-2">{t('loading')}</p>
        </Card>
      </Base>
    );
  }

  const event = data.event || {};
  const canManage = !!data.can_manage;
  const when = [shortDate(event.date, i18n.language), wallClockTime(event.date)]
    .filter(Boolean).join(' · ');

  return (
    <Base activeNav="home">
      <Card>
        <Back to={`/host-event/${eventId}`} />
        <Title my="1" className="mt-3">
          <Icon name="settings" theme="primary" className="me-2" />
          {event.name || t('eventSettings')}
        </Title>
        <p className="bravos-q__hint mb-2">{when}</p>
        <p className="bravos-q__hint mb-2">{t('eventSettingsNote')}</p>
        {/* ★ Once it has happened these stop being settings and start being the record of what
            it WAS. The server refuses the save (see put_company_event), so the chips and the
            switches are locked here rather than left to fail — publishing, cancelling and
            deleting are decisions about the event and stay live. */}
        {event.is_past && <p className="ce-note ce-note--warn">{t('eventIsPast')}</p>}

        <fieldset className="ce-lock" disabled={busy || !!event.is_past}>
          {/* The company's own vocabulary — townhall, workshop, family day. Renaming one is a
              different screen; which ones THIS event carries is here. */}
          <span className="bravos-form__label">{t('eventTags')}</span>
          <TagChips
            tags={data.tag_catalog || []}
            selected={tags}
            disabled={busy}
            onToggle={(id) => setTags((list) => (list.includes(id)
              ? list.filter((x) => x !== id) : [...list, id]))}
          />

          {/* What it is FOR — the company's own themes, values and UN goals, picked from what it
              already declares under Programmes. Under the categories because the two are the
              same kind of answer: the company's vocabulary, not this one event's words.
              Nothing to pick from on a public event: there is no company declaring any of it. */}
          {isCompanyEvent && (
            <div className="mt-3">
              <PurposePicker
                catalog={data.purpose_catalog}
                picks={purpose}
                onToggle={togglePurpose}
                disabled={busy}
              />
            </div>
          )}

          <p className="bravos-form__label mt-3">{t('registrationTitle')}</p>
          <label className="ce-check">
            <input
              type="checkbox"
              checked={!!reg.registration_enabled}
              onChange={(e) => setReg({ ...reg, registration_enabled: e.target.checked })}
            />
            <span>
              {t('registrationEnabled')}
              <span className="ce-check__hint">{t('registrationEnabledHint')}</span>
            </span>
          </label>
          <label className="ce-check">
            <input
              type="checkbox"
              checked={!!reg.show_registered_count}
              onChange={(e) => setReg({ ...reg, show_registered_count: e.target.checked })}
            />
            <span>{t('showRegisteredCount')}</span>
          </label>
          <label className="ce-check">
            <input
              type="checkbox"
              checked={!!reg.show_registered_names}
              onChange={(e) => setReg({ ...reg, show_registered_names: e.target.checked })}
            />
            <span>
              {t('showRegisteredNames')}
              <span className="ce-check__hint">{t('showRegisteredNamesHint')}</span>
            </span>
          </label>

          {/* A CLUB's event: every colleague sees it, only the club's members may register —
              the teaser that recruits. Only drawn on a club's event; the server ignores it on
              any other. */}
          {data.is_club_event && (
            <label className="ce-check">
              <input
                type="checkbox"
                checked={!!reg.members_only}
                onChange={(e) => setReg({ ...reg, members_only: e.target.checked })}
              />
              <span>
                {t('clubMembersOnlyLabel')}
                <span className="ce-check__hint">{t('clubMembersOnlyHint')}</span>
              </span>
            </label>
          )}

          {/* Who may come at all. A setup switch like the three above, saved by the same PUT —
              and it changes what publishing DOES, which is why it sits beside the publish
              button rather than on the editor. */}
          <label className="ce-check">
            <input
              type="checkbox"
              checked={!!reg.is_invite_only}
              onChange={(e) => setReg({ ...reg, is_invite_only: e.target.checked })}
            />
            <span>
              {t('inviteOnly')}
              <span className="ce-check__hint">{t('inviteOnlyHint')}</span>
            </span>
          </label>
          {/* Only a question once the event IS invite-only: it decides whether the rest of the
              company sees a locked row or nothing at all. Unticked is the safe answer, so it is
              phrased as the extra step you take deliberately. */}
          {reg.is_invite_only && (
            <label className="ce-check ce-check--nested">
              <input
                type="checkbox"
                checked={!!reg.hide_from_non_invited}
                onChange={(e) => setReg({ ...reg, hide_from_non_invited: e.target.checked })}
              />
              <span>
                {t('hideFromNonInvited')}
                <span className="ce-check__hint">{t('hideFromNonInvitedHint')}</span>
              </span>
            </label>
          )}
          {reg.is_invite_only && canManage && (
            <button
              type="button"
              className="bravos-copy mt-2"
              onClick={() => navigate(`/company-event/${eventId}/people?tab=invites`)}
            >
              <Icon name="users" /> {t('manageGuestList')}
            </button>
          )}

          {/* The only mail on this event that goes out without anybody pressing anything, so it
              is the only one with a switch here. Ticked unless somebody untickes it — the whole
              point is that nobody has to remember. */}
          <label className="ce-check">
            <input
              type="checkbox"
              checked={!!reg.send_day_before_mail}
              onChange={(e) => setReg({ ...reg, send_day_before_mail: e.target.checked })}
            />
            <span>
              {t('sendDayBeforeMail')}
              <span className="ce-check__hint">{t('sendDayBeforeMailHint')}</span>
            </span>
          </label>
        </fieldset>

        {/* Both tick-boxes modify the buttons under them: the series one widens saving and
            publishing to every future date, the notify one is what turns publishing into
            invitations. */}
        {event.series_size > 1 && (
          <label className="ce-check">
            <input
              type="checkbox"
              checked={applySeries}
              onChange={(e) => setApplySeries(e.target.checked)}
            />
            <span>
              {t('applyToSeries', { n: event.series_size })}
              <span className="ce-check__hint">{t('applyToSeriesHint')}</span>
            </span>
          </label>
        )}
        <label className="ce-check">
          <input
            type="checkbox"
            checked={notify}
            onChange={(e) => setNotify(e.target.checked)}
          />
          <span>
            {t('notifyOurPeople')}
            <span className="ce-check__hint">{t('notifyOurPeopleHint')}</span>
          </span>
        </label>

        {!event.is_past && (
          <button type="button" className="bravos-submit" onClick={saveSetup} disabled={busy}>
            {busy ? t('pleaseWait') : t('save')}
          </button>
        )}

        {!event.is_cancelled && (
          event.is_public ? (
            <button
              type="button"
              className="bravos-submit bravos-submit--ghost"
              onClick={() => publish(false)}
              disabled={busy}
            >
              {t('backToDraft')}
            </button>
          ) : (
            <button
              type="button"
              className="bravos-submit bravos-submit--ghost"
              onClick={() => publish(true)}
              disabled={busy}
            >
              <Icon name="paper-plane" /> {t('publishEvent')}
            </button>
          )
        )}

        <button
          type="button"
          className="bravos-submit bravos-submit--ghost"
          onClick={() => setCancelled(!event.is_cancelled)}
          disabled={busy}
        >
          {event.is_cancelled ? t('uncancelEvent') : t('cancelEvent')}
        </button>
        {/* Said out loud on the screen rather than only in the confirm box, because the two are
            asymmetric and the difference is what people get wrong: cancelling keeps everything
            and is undoable, deleting keeps nothing and is not. */}
        <p className="bravos-q__hint">{t('cancelEventHint')}</p>
        {/* Cancelling is the host's too — they are the one who knows it is off. DELETING is not
            running an event: it is irreversible and it is the admin's. */}
        {canManage && (
          <>
            <button
              type="button"
              className="bravos-submit bravos-submit--ghost"
              onClick={remove}
              disabled={busy}
            >
              <Icon name="trash" /> {t('deleteEvent')}
            </button>
            <p className="bravos-q__hint">{t('deleteEventHint')}</p>
          </>
        )}

        {notice && <p className="bravos-cal__notice">{notice}</p>}
      </Card>
    </Base>
  );
};

export default HostEventSettings;
